/**
 * Calendar arithmetic for the date block, done entirely on `YYYY-MM-DD` strings.
 *
 * The one rule this file exists to keep: **the answer is never a `Date`.** pro-core stores a plain
 * calendar date with no time and no zone, precisely so "I can start on the 3rd" cannot drift to the
 * 2nd for someone in another offset. A `Date` is an instant, and every conversion in and out of one
 * is a chance to reintroduce exactly that. Dates are constructed here only to ask the calendar
 * questions — what weekday is the 1st, how many days has February — and always with the local
 * `(y, m, d)` constructor, never by parsing a string, because `new Date('2026-09-01')` is parsed as
 * UTC midnight and renders as the previous day for anyone west of it.
 */

/** ISO calendar date. The only shape pro-core accepts and the only one this module produces. */
export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type CalendarMonth = {
  year: number;
  /** 1-based, as it is written. */
  month: number;
};

export type CalendarCell = {
  /** `YYYY-MM-DD`, or null for the blank cells that pad the first week. */
  iso: string | null;
  day: number | null;
  selectable: boolean;
  /** A stable key for a blank cell, so the grid never keys anything on an array index. */
  padKey?: string;
};

const pad = (value: number): string => String(value).padStart(2, '0');

export const toIso = (year: number, month: number, day: number): string =>
  `${year}-${pad(month)}-${pad(day)}`;

export const parseIso = (
  iso: string | null | undefined
): (CalendarMonth & { day: number }) | null => {
  if (!iso || !ISO_DATE.test(iso)) {
    return null;
  }
  const [year, month, day] = iso.split('-').map(Number);
  return { year, month, day };
};

/** Days in a month, from the calendar itself rather than a table with a leap-year exception. */
export const daysInMonth = (year: number, month: number): number =>
  new Date(year, month, 0).getDate();

/**
 * Which column the 1st falls in, Monday-first.
 *
 * `getDay()` is Sunday-first, which would put every German week a day out — and this form is
 * answered overwhelmingly in a Monday-first locale.
 */
const leadingBlanks = (year: number, month: number): number =>
  (new Date(year, month - 1, 1).getDay() + 6) % 7;

/**
 * Move by whole months without ever landing on an invalid date.
 *
 * Adding a month to 31 January by incrementing the month number gives 31 February; this works in
 * (year, month) space and only resolves a day afterwards, so it cannot.
 */
export const shiftMonth = (
  from: CalendarMonth,
  delta: number
): CalendarMonth => {
  const zeroBased = from.year * 12 + (from.month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
};

/**
 * ISO strings compare correctly as text — fixed width, zero-padded, most significant first — so the
 * whole window check is string comparison and never touches a Date.
 */
export const isWithin = (
  iso: string,
  minDate: string | null,
  maxDate: string | null
): boolean => {
  if (minDate && iso < minDate) {
    return false;
  }
  return !(maxDate && iso > maxDate);
};

/** The grid for one month: leading blanks, then every day, marked selectable or not. */
export const monthGrid = (
  { year, month }: CalendarMonth,
  minDate: string | null,
  maxDate: string | null
): CalendarCell[] => {
  const cells: CalendarCell[] = [];
  for (let blank = 0; blank < leadingBlanks(year, month); blank += 1) {
    cells.push({
      iso: null,
      day: null,
      selectable: false,
      padKey: `pad-${year}-${month}-${blank}`
    });
  }
  for (let day = 1; day <= daysInMonth(year, month); day += 1) {
    const iso = toIso(year, month, day);
    cells.push({ iso, day, selectable: isWithin(iso, minDate, maxDate) });
  }
  return cells;
};

/**
 * Whether the month before or after holds anything answerable.
 *
 * Used to disable the arrows rather than letting someone page through years of greyed-out days
 * looking for one they can pick.
 */
export const hasSelectableMonth = (
  target: CalendarMonth,
  minDate: string | null,
  maxDate: string | null
): boolean => monthGrid(target, minDate, maxDate).some(cell => cell.selectable);

/**
 * The month the calendar should open on.
 *
 * The answer if there is one, otherwise the start of the allowed window — never "today", which for
 * a window that begins next month would open on a page with nothing to choose.
 */
export const initialMonth = (
  value: string | null,
  minDate: string | null,
  now: Date
): CalendarMonth => {
  const chosen = parseIso(value) ?? parseIso(minDate);
  return chosen
    ? { year: chosen.year, month: chosen.month }
    : { year: now.getFullYear(), month: now.getMonth() + 1 };
};
